import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TypedSignaturePad } from '@/components/common/TypedSignaturePad';
import { API } from '@/utils/constants';
import { formatDate } from '@/utils/date';

/**
 * Public Signature Page
 * Allows customers to view job details and sign without authentication
 */
const PublicSignaturePage = () => {
  const { token } = useParams();
  const [job, setJob] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    fetchJobByToken();
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchJobByToken = async () => {
    try {
      const response = await axios.get(`${API}/signature/${token}`);
      setJob(response.data);
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Invalid or expired signature link');
    } finally {
      setLoading(false);
    }
  };

  // Send signature (typed name → cursive PNG data URL rendered client-side)
  // plus audit metadata to the backend. Backend also captures IP + UA server-
  // side from request headers.
  const handleSubmitSignature = async (signatureData, meta = {}) => {
    try {
      setSubmitting(true);
      await axios.post(`${API}/signature/${token}`, {
        signature: signatureData,
        signature_type: 'typed',
        typed_name: meta.typed_name || '',
      });
      // Update local job state directly — we can't refetch by token because
      // the backend invalidates it on use (returns 404 after signing).
      setJob((prev) => prev ? {
        ...prev,
        signature: signatureData,
        signature_date: new Date().toISOString(),
        signature_type: 'typed',
        signature_typed_name: meta.typed_name || '',
      } : prev);
      toast.success('Signature submitted successfully! Thank you.');
    } catch (error) {
      console.error('Error submitting signature:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit signature');
      throw error; // Re-throw so TypedSignaturePad re-enables its Submit button
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="public-signature-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="public-signature-page">
        <Card className="signature-card">
          <CardHeader>
            <CardTitle>Invalid Link</CardTitle>
          </CardHeader>
          <CardContent>
            <p>This signature link is invalid or has expired.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="public-signature-page">
      <div className="signature-branding">
        <img src="/logo.jpg" alt="Total Data Recall" className="signature-logo" />
        <h1>Total Data Recall</h1>
      </div>

      <Card className="signature-card">
        <CardHeader>
          <CardTitle>Job Card #{job.job_number}</CardTitle>
          <CardDescription>Please review and sign below</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="job-card-details">
            <div className="detail-row">
              <strong>Customer Name:</strong> {job.customer_name}
            </div>
            <div className="detail-row">
              <strong>Address:</strong> {job.address_line1}, {job.suburb}, {job.state} {job.postcode}
            </div>
            <div className="detail-row">
              <strong>Email:</strong> {job.email}
            </div>
            <div className="detail-row">
              <strong>Mobile:</strong> {job.mobile}
            </div>
            <div className="detail-row">
              <strong>Job Type:</strong> {job.job_type}
            </div>
            <div className="detail-row">
              <strong>Brand:</strong> {job.brand}
            </div>
            {job.model_number && (
              <div className="detail-row">
                <strong>Model:</strong> {job.model_number}
              </div>
            )}
            {job.serial_number && (
              <div className="detail-row">
                <strong>Serial:</strong> {job.serial_number}
              </div>
            )}
            {job.job_description && (
              <div className="detail-row">
                <strong>Description:</strong> {job.job_description}
              </div>
            )}
            <div className="detail-row">
              <strong>Date In:</strong> {formatDate(job.date_in)}
            </div>
            <div className="detail-row">
              <strong>Total Amount:</strong> ${job.total_amount?.toFixed(2) || '0.00'}
            </div>
          </div>

          {/* Terms & Conditions Section */}
          {settings?.terms_and_conditions && (
            <div className="terms-conditions" style={{
              marginTop: '2rem',
              padding: '1.5rem',
              backgroundColor: '#f8fafc',
              border: '2px solid #e2e8f0',
              borderRadius: '0.5rem',
              marginBottom: '1.5rem'
            }}>
              <h4 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                Terms & Conditions
              </h4>
              <div style={{
                whiteSpace: 'pre-wrap',
                fontSize: '0.875rem',
                lineHeight: '1.6',
                color: '#475569',
                maxHeight: '300px',
                overflowY: 'auto',
                padding: '0.5rem',
                backgroundColor: 'white',
                borderRadius: '0.25rem'
              }}>
                {settings.terms_and_conditions}
              </div>
            </div>
          )}

          {/* Terms Agreement Checkbox (only show if not signed) */}
          {!job.signature && settings?.terms_and_conditions && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: '#fef3c7',
              border: '2px solid #fbbf24',
              borderRadius: '0.5rem'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.95rem' }}>
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  style={{ 
                    width: '20px', 
                    height: '20px', 
                    marginRight: '0.75rem',
                    cursor: 'pointer'
                  }}
                  data-testid="agree-terms-checkbox"
                />
                <span style={{ fontWeight: '500', color: '#78350f' }}>
                  I have read and agree to the Terms & Conditions above
                </span>
              </label>
            </div>
          )}

          {job.signature ? (
            <div className="signature-completed">
              <h4>✓ Signed</h4>
              <img src={job.signature} alt="Signature" className="signature-image" />
              <p className="signature-date">
                Signed on: {new Date(job.signature_date).toLocaleString()}
              </p>
              <p className="thank-you">Thank you! This job card has been signed.</p>
            </div>
          ) : (
            <div className="signature-area">
              <h4>Customer Signature</h4>
              <TypedSignaturePad
                onSave={handleSubmitSignature}
                initialName={job.customer_name || ''}
                disabled={!!settings?.terms_and_conditions && !agreedToTerms}
                disabledReason="Please tick the Terms & Conditions box above before signing."
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PublicSignaturePage;
